import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
    CardVillageClientV2,
    Col,
    Container,
    Row,
    Search,
    Spinner,
    TimedAlert,
    TopBar,
    CardVillagePotentialClient,
    SearchableTab
} from '../../components'
import NoVillagePage from '../../components/noVillage'
import { InputErrorMessage } from 'components/enrollForm/InputErrorMessage'
import {
    clientQuickSearchValidationRules,
    constants,
    filterHandler,
    searchHandler,
    buildClientFilters,
    getConnectUser,
    visitedAnytime,
    notInterested,
    sortPotentialClients,
    hasActiveBulkEnrollmentSession
} from '../../helpers'

import {
    useBindDispatch,
    useScreenSize,
    useAnalytics,
    useBulkEnrollmentAnalytics
} from 'hooks'
import { buildPotentialClientFilter } from './villageFilterOptions'
import { colors } from 'styles/colors'
import { useProcessedClientList } from 'hooks/useProcessClientLists'
import ClientFilter from './components/filters/ClientFilter'
import { BulkEnrollmentSearchButton } from './components/BulkEnrollmentSearchButton'
import { VillageEnrollmentQr } from './components/VillageEnrollmentQr'
import './village.scss'

/**
 * @param {{ goBackTo?: string, showBack?: boolean, showEnrollmentQr?: boolean }} props
 */

export const VillageClient = ({ goBackTo, showBack, showEnrollmentQr }) => {
    const { villageId } = useParams()
    const { trackEvent } = useAnalytics()
    const { trackAbandoned } = useBulkEnrollmentAnalytics(villageId)
    const inputSearchRef = useRef(null)
    const { t } = useTranslation()
    const screenSize = useScreenSize()
    const [clientListData, setClientListData] = useState([])
    const [prevClientListData, setPrevClientListData] = useState([])
    const [prevSearchValue, setPrevSearchValue] = useState({ search: '' })
    const [tabLoader, setTabLoader] = useState(true)
    const [nbOfIncompleteClients, setNbOfIncompleteClients] = useState(0)
    const [currentSearchValue, setCurrentSearchValue] = useState({ search: '' })
    const [hasUserSearched, setHasUserSearched] = useState(false)
    const [showLoadingText, setShowLoadingText] = useState(false)
    const { MOBILE_SIZE, VILLAGE_PAGE_TABS } = constants
    const { openVillagePageData } = useSelector(
        (state) => state.openVillagePage
    )
    const [pageParams, setPageParams] = useState({
        ...openVillagePageData
    })
    const { search } = useLocation()
    const initialActiveTab = pageParams?.clientTab
        ? pageParams.clientTab
        : VILLAGE_PAGE_TABS.PREVIOUS_CLIENT_TAB
    const fromTab = parseInt((search || '').split('from_tab=')[1])

    const [activeTab, setActiveTab] = useState({
        key: fromTab || initialActiveTab
    })

    const {
        villageClientListHandler,
        transactionsHandler,
        openVillagePageHandler,
        navbarHandler,
        clearClientSearchHandler,
        storeVillageOrdersHandler,
        fetchInteractionsHandler,
        filterSelectorHandler,
        initializeBulkEnrollmentHandler
    } = useBindDispatch()
    const {
        loading: loadingClientList,
        clientList,
        error: clientListError
    } = useSelector((state) => state.villageClientList[villageId] || {})
    const { interactions } = useSelector(
        ({ interactions }) => interactions[villageId] || {}
    )
    const { previousClientList, loading: loadingPreviousClientList } =
        useSelector((state) => state.villagePreviousClientList[villageId] || {})

    const { selectedFilter } = useSelector((state) => state.filterSelection)
    const { veVillages = [] } = getConnectUser()
    const navigate = useNavigate()

    useEffect(() => {
        if (hasActiveBulkEnrollmentSession()) {
            trackAbandoned()
        }
    }, [])

    // get village name
    const foundVillage = veVillages.find(
        ({ sf_id: sfId }) => sfId === villageId
    )
    const showBackBtn = showBack || veVillages.length > 1 || !foundVillage
    const isMobile = screenSize.screenWidth <= MOBILE_SIZE

    const potentialClientFilter = buildPotentialClientFilter(t)

    // handle scroll position
    const storeScrollPosition = () => {
        localStorage.setItem(
            'scroll_pos',
            JSON.stringify({ scroll: document.documentElement.scrollTop })
        )
    }

    // handle previous client details page navigation
    const prevClientSelection = (clientId) => {
        // log scroll position
        storeScrollPosition()
        navigate(`/village/${villageId}/client/${clientId}/previous`)
    }

    const isPotentialClientTab =
        activeTab.key === VILLAGE_PAGE_TABS.PREVIOUS_CLIENT_TAB

    const filteringConditions = useMemo(
        () => buildClientFilters(t, interactions),
        [interactions]
    )

    const getFilterCondition = (filterName) => {
        if (typeof filterName === 'string') {
            return filteringConditions[filterName]
        }
        return filteringConditions.previousYear(filterName)
    }

    const processClientListParams = {
        activeClients: clientList?.length ? [...clientList] : [],
        potentialClients: previousClientList?.length
            ? [...previousClientList]
            : [],
        selectedFilter,
        filteringCondition: getFilterCondition,
        isActiveClientTab: !isPotentialClientTab,
        isPotentialClientTab
    }

    const processClientListValue = useProcessedClientList(
        processClientListParams
    )

    const createClientCard = (
        {
            client_code: clientCode,
            fullname,
            phone,
            balance,
            all_enrollment: allEnrollment,
            payment_progress: paymentProgress,
            last_payment_date: lastPaymentDate,
            sex,
            age,
            duplicate,
            categories = []
        },
        index
    ) => {
        const addSeparator = index === nbOfIncompleteClients
        return (
            <div key={clientCode} className="single-client-card">
                {addSeparator && <br />}
                <CardVillageClientV2
                    sex={sex}
                    age={age}
                    clientCode={clientCode}
                    fullname={fullname}
                    phoneNumber={phone}
                    lastPaymentDate={lastPaymentDate}
                    paymentProgress={paymentProgress}
                    remainingAmount={allEnrollment - balance}
                    balance={balance}
                    onClick={() => handleClientSelection(clientCode)}
                    duplicate={duplicate}
                    categories={categories}
                />
            </div>
        )
    }

    const createPotentialClientCard = (client) => (
        <CardVillagePotentialClient
            {...client}
            onClickHandler={prevClientSelection}
        />
    )

    const sendSearchAnalyticsEvent = (tab) => {
        trackEvent({
            category:
                tab.key === 0
                    ? constants.ANALYTICS.VILLAGE_POTENTIAL
                    : constants.ANALYTICS.VILLAGE_CLIENT,
            action: constants.ANALYTICS.ACTIONS.SEARCH
        })
    }

    const handleSearchBlur = () => {
        sendSearchAnalyticsEvent(activeTab)
        if (inputSearchRef.current) {
            inputSearchRef.current.removeEventListener('blur', handleSearchBlur)
        }
        setHasUserSearched(false)
    }

    const addSearchOnBlurListener = () => {
        if (inputSearchRef.current) {
            inputSearchRef.current.addEventListener('blur', handleSearchBlur)
        }
    }

    const getClientListKey = useCallback(
        (clients) => clients.map((c) => c.client_code).join(','),
        []
    )

    const previousClientsKey = useMemo(
        () => getClientListKey(prevClientListData),
        [prevClientListData]
    )

    const sortedClientListKey = useMemo(
        () => getClientListKey(clientListData),
        [clientListData]
    )

    // fetch client list by the villageId
    useEffect(() => {
        // clear client details from local storage
        localStorage.removeItem('client_details')

        villageClientListHandler({ villageId })
        // set page title
        document.title = t('village.pageTitle')

        // display navbar
        navbarHandler(constants.SHOW_NAVBAR)

        // reset any client search result
        clearClientSearchHandler()

        const scrollPosition =
            JSON.parse(localStorage.getItem('scroll_pos'))?.scroll || 0
        document.documentElement.scrollTop = scrollPosition
    }, [])

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowLoadingText(true)
        }, 2000)

        return () => clearTimeout(timer)
    }, [])

    useEffect(() => {
        storeVillageOrdersHandler([{ sf_id: villageId }])
        fetchInteractionsHandler({
            villageIds: [villageId],
            surveyNames: [constants.FARMER_AWARENESS_SURVEY_NAME],
            surveyVersion: constants.FARMER_AWARENESS_SURVEY_VERSION
        })
        transactionsHandler({ villageId })
    }, [])

    // reset home client click data
    useEffect(() => {
        if (openVillagePageData?.filterOptions?.length) {
            openVillagePageHandler({
                filterOptions: undefined,
                villageId: '',
                clientTab: ''
            })
        }
        if (pageParams?.clientTab !== activeTab.key) {
            setPageParams()
        }
    }, [openVillagePageData, activeTab])

    // set client list state when client list updates
    useEffect(() => {
        if (clientList) {
            setClientListData(clientList)
        }

        if (previousClientList) {
            setPrevClientListData(previousClientList)
        }

        // filter option from params
        if (pageParams?.filterOptions) {
            filterSelectionHandler([...pageParams.filterOptions])
        }
    }, [clientList, previousClientList])

    useEffect(() => {
        if (tabLoader) {
            setTabLoader(false)
        }
    }, [activeTab])

    useEffect(() => {
        if (!(prevSearchValue.search || currentSearchValue.search)) {
            setClientListData(processClientListValue.processedClientList)

            setPrevClientListData(
                processClientListValue.processedPotentialClientList
            )
            setNbOfIncompleteClients(
                processClientListValue.nbOfIncompleteClients
            )
        }
    }, [
        prevSearchValue,
        currentSearchValue,
        selectedFilter,
        processClientListValue.nbOfIncompleteClients
    ])

    const tabClientConfig = {
        0: {
            data: prevClientListData,
            setData: setPrevClientListData,
            reducerData: previousClientList
        },
        1: {
            data: clientListData,
            setData: setClientListData,
            reducerData: clientList
        }
    }

    const prevClientVisitedMerged = useMemo(() => {
        const getClientVisitedStatus = visitedAnytime(interactions)
        const getIsNotInterestedStatus = notInterested(interactions)

        const updatedPreviousClients = prevClientListData?.map((client) => {
            return {
                ...client,
                isVisited: getClientVisitedStatus(client),
                isNotInterested: getIsNotInterestedStatus(client)
            }
        })

        return sortPotentialClients(updatedPreviousClients)
    }, [prevClientListData, interactions])

    const [getSearchQueryError] = clientQuickSearchValidationRules().query

    // handle client list search
    const clientSearchHandler = (
        e,
        clientReducer,
        clientState,
        searchSetState
    ) => {
        e.preventDefault()
        if (!hasUserSearched) {
            addSearchOnBlurListener()
            setHasUserSearched(true)
        }
        const { value } = e.target
        searchSetState({ search: value })
        if (!getSearchQueryError(value) && value.trim().length > 2) {
            return clientState(
                searchHandler(clientReducer || [], {
                    phone: value
                })
            )
        }
        clientState(value ? [] : clientReducer || [])
    }

    const handleClientSelection = (clientId) => {
        storeScrollPosition()
        navigate(`/village/${villageId}/client/${clientId}`)
    }

    // handle filter option selection
    const filterSelectionHandler = (selectedFilters = []) => {
        if (currentSearchValue?.search || prevSearchValue.search) {
            return
        }
        if (selectedFilters.length === 0) {
            selectedFilters = [t('all')]
        }
        let filteredClientData = []

        const allActiveTabClient = tabClientConfig[activeTab?.key]?.reducerData

        const clientListFilter = (filterCondition) => {
            if (!allActiveTabClient) {
                return []
            }
            return filterHandler(allActiveTabClient, filterCondition)
        }

        if (activeTab?.key > 0) {
            filteredClientData = []
            const updateClientList = tabClientConfig[activeTab.key].setData
            // get client list
            const clientList = tabClientConfig[activeTab.key].data
            if (selectedFilters.includes(t('all'))) {
                return updateClientList(clientList)
            }
            try {
                selectedFilters.forEach((singleSelection) => {
                    filteredClientData = [
                        ...clientListFilter(
                            filteringConditions[singleSelection]
                        ),
                        ...filteredClientData
                    ]
                    return filteredClientData
                })
                filteredClientData = [...new Set(filteredClientData)]
            } catch (error) {}

            updateClientList(filteredClientData)
        } else if (activeTab?.key === 0) {
            filteredClientData = []
            const updatePrevClientList = tabClientConfig[activeTab.key].setData
            // get prev client list
            const prevClientList =
                tabClientConfig[activeTab.key].reducerData || []

            if (selectedFilters.includes(potentialClientFilter.ALL)) {
                return updatePrevClientList(prevClientList)
            }

            if (
                selectedFilters.includes(
                    potentialClientFilter.VISITED_THIS_WEEK
                )
            ) {
                const filterCondition =
                    filteringConditions[potentialClientFilter.VISITED_THIS_WEEK]
                return updatePrevClientList(
                    prevClientList.filter(filterCondition)
                )
            }

            selectedFilters.forEach((singleSelection) => {
                if (
                    // We assume if the selected filter is not one of the predefined filters,
                    // it is a dynamically generated previous year filter.
                    !Object.values(potentialClientFilter).includes(
                        singleSelection
                    )
                ) {
                    const clientListFiltered = clientListFilter(
                        filteringConditions.previousYear(singleSelection)
                    )
                    filteredClientData = [
                        ...filteredClientData,
                        ...clientListFiltered
                    ]
                    return filteredClientData
                }

                filteredClientData = [
                    ...filteredClientData,
                    ...clientListFilter(filteringConditions[singleSelection])
                ]
                return filteredClientData
            })

            filteredClientData = [...new Set(filteredClientData)]
            updatePrevClientList(filteredClientData)
        }
    }

    const handleBackBtn = () => {
        navigate(goBackTo || (veVillages.length > 1 ? '/village' : -1))
    }

    const openBulkEnrollmentPackageSelection = () => {
        initializeBulkEnrollmentHandler(villageId)
        navigate(`/village/${villageId}/bulk-enrollment/packages`)
    }

    const searchQueryError = getSearchQueryError(
        activeTab.key === VILLAGE_PAGE_TABS.CURRENT_CLIENT_TAB
            ? currentSearchValue.search
            : prevSearchValue.search
    )

    const searchBarRender = (
        <Col className="search-bar-container" md={12}>
            <div className="search-bar-with-bulk">
                <div className="search-bar-with-bulk__input">
                    {activeTab.key === VILLAGE_PAGE_TABS.CURRENT_CLIENT_TAB ? (
                        <Search
                            placeholder={t('searchInputPlaceholder')}
                            onChange={(e) =>
                                clientSearchHandler(
                                    e,
                                    clientList,
                                    setClientListData,
                                    setCurrentSearchValue
                                )
                            }
                            iconSize="30"
                            iconPos="left"
                            style="rounded"
                            inputRef={inputSearchRef}
                            value={currentSearchValue.search}
                            data-testid="search-input"
                            id="client-list-search"
                        />
                    ) : (
                        <Search
                            placeholder={t('searchInputPlaceholder')}
                            onChange={(e) =>
                                clientSearchHandler(
                                    e,
                                    previousClientList,
                                    setPrevClientListData,
                                    setPrevSearchValue
                                )
                            }
                            iconColor={colors.maroon}
                            iconSize="30"
                            iconPos="left"
                            style="rounded"
                            inputRef={inputSearchRef}
                            value={prevSearchValue.search}
                            data-testid="search-input"
                            id="client-list-search"
                        />
                    )}
                </div>
                <BulkEnrollmentSearchButton
                    onClick={openBulkEnrollmentPackageSelection}
                />
            </div>
            {searchQueryError && (
                <InputErrorMessage errorMessage={searchQueryError} />
            )}
        </Col>
    )

    const clientPageRender = () => {
        return (
            <>
                <div className="sticky-header" id="sticky-header">
                    <TopBar back={showBackBtn} backNavigation={handleBackBtn}>
                        {foundVillage ? foundVillage?.name : t('error')}
                    </TopBar>
                </div>
                {foundVillage ? (
                    <div>
                        <Container>
                            <Row>
                                <Col
                                    md={12}
                                    className={isMobile ? 'remove-padding' : ''}
                                >
                                    <div className="village--client-list-container">
                                        {showEnrollmentQr && (
                                            <VillageEnrollmentQr
                                                villageCode={foundVillage?.code}
                                                villageName={foundVillage?.name}
                                                className="enrollment-qr-home--compact"
                                            />
                                        )}
                                        {searchBarRender}
                                        <SearchableTab
                                            key={`${sortedClientListKey}|${previousClientsKey}`}
                                            tabs={[
                                                {
                                                    text: t(
                                                        'village.potentials'
                                                    ),
                                                    placeholderText: t(
                                                        'village.villageSearchPlaceholder'
                                                    ),
                                                    noResultsText: t(
                                                        'village.noPotentialClient'
                                                    ),
                                                    extraClass: 'maroon',
                                                    items: prevClientVisitedMerged,
                                                    renderItem: (client) =>
                                                        createPotentialClientCard(
                                                            client
                                                        ),
                                                    tabInnerComponent: (
                                                        <ClientFilter
                                                            activeTab={{
                                                                key: VILLAGE_PAGE_TABS.PREVIOUS_CLIENT_TAB
                                                            }}
                                                            searchValue={
                                                                prevSearchValue
                                                            }
                                                            resetSearchValue={() =>
                                                                setPrevSearchValue(
                                                                    {
                                                                        search: ''
                                                                    }
                                                                )
                                                            }
                                                        />
                                                    )
                                                },
                                                {
                                                    text: t('village.clients'),
                                                    placeholderText: t(
                                                        'village.villageSearchPlaceholder'
                                                    ),
                                                    noResultsText: t(
                                                        'village.noActiveClient'
                                                    ),
                                                    extraClass: 'green',
                                                    items: clientListData,
                                                    renderItem: (client) =>
                                                        createClientCard(
                                                            client
                                                        ),
                                                    tabInnerComponent: (
                                                        <ClientFilter
                                                            activeTab={{
                                                                key: VILLAGE_PAGE_TABS.CURRENT_CLIENT_TAB
                                                            }}
                                                            searchValue={
                                                                currentSearchValue
                                                            }
                                                            resetSearchValue={() =>
                                                                setCurrentSearchValue(
                                                                    {
                                                                        search: ''
                                                                    }
                                                                )
                                                            }
                                                        />
                                                    )
                                                }
                                            ]}
                                            tabBackgroundClassName="tab-body"
                                            onActiveTabChange={(activeTab) => {
                                                setCurrentSearchValue({
                                                    search: ''
                                                })
                                                setPrevSearchValue({
                                                    search: ''
                                                })
                                                filterSelectorHandler({
                                                    selectedFilter: undefined
                                                })
                                                setActiveTab({
                                                    key: activeTab
                                                })
                                            }}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                ) : (
                    <NoVillagePage text={t('noVillage')} />
                )}
            </>
        )
    }

    // Show the spinner whenever the list is loading, including the first load
    // after a refresh when Redux has no cached list yet. Otherwise the page
    // renders with empty tabs and 0 counts until the fetch completes.
    const loadingComponent = (loadingClientList ||
        loadingPreviousClientList) && (
        <>
            <Spinner
                size={'90'}
                pageSpinner
                loadingMessage={
                    (showLoadingText && t('village.partialLoaderMsg')) || ''
                }
            />
        </>
    )
    const clientErrorComponent = clientListError && (
        <TimedAlert type="floating" status="error">
            {clientListError}
        </TimedAlert>
    )

    const villageClients =
        loadingComponent || clientErrorComponent || clientPageRender()

    return villageClients
}
